import React, { useState, useEffect } from 'react'
import { Password, Alert, Typography, Button, Icon } from 'components'
import { useDispatch } from 'store/store'
import { useNavigate } from 'react-router-dom'
import {
  hasLowerCase,
  hasMinLength,
  hasNumber,
  hasSpecialCharacter,
  hasUpperCase
} from 'commons/utils/password'
import { CodeResponseStorage } from './components/types/responses'
import {
  listSecurityParameters,
  SecurityParameters,
  updatePasswordRequest
} from 'services/forgetPassword'
import { ROUTES } from 'constants/urls'
import * as S from './styles'
import { toast } from 'react-toastify'
import { AuthLayout } from 'layouts/AuthLayout'
import { clearState } from 'store/AuthContext/authSlice'
import { theme } from 'styles/theme/hav-theme'

const { forgetPassword } = ROUTES

enum InputsNames {
  NEW = 'newPassword',
  CONFIRM = 'confirmPassword'
}

const IconCheck = {
  true: <Icon icon="task-alt" color={theme.colors.alert.success.dark} size="15" />,
  false: <Icon icon="chevron-right" color={theme.colors.neutral.medium} size="15" />
}

type CodeStorage = { code: string } & CodeResponseStorage

const ResetPassword: React.FC = () => {
  const dispatch = useDispatch()
  const [newPassword, setNewPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [isDisabled, setIsDisabled] = useState<boolean>(true)
  const [showPastPasswordError, setShowPastPasswordError] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [securityParameters, setSecurityParameters] = useState<SecurityParameters[]>([])
  const navigate = useNavigate()

  const email = localStorage.getItem('resetPassword')
  const emailFromStorage: CodeStorage = email !== null && JSON.parse(email)

  const minLengthPassword =
    securityParameters.find((item) => item.cd_parm === 701)?.vl_parm || '8'

  useEffect(() => {
    if (emailFromStorage.return_chav !== 'Sucesso') {
      navigate(forgetPassword.getLink('validateCode'))
    }
    listSecurityParameters().then((response) => {
      setSecurityParameters(response)
    })
  }, [])

  useEffect(() => {
    if (
      newPassword === confirmPassword &&
      hasNumber(newPassword, securityParameters) &&
      hasSpecialCharacter(newPassword, securityParameters) &&
      hasUpperCase(newPassword, securityParameters) &&
      hasMinLength(newPassword, Number(minLengthPassword)) &&
      hasLowerCase(newPassword, securityParameters)
    ) {
      setIsDisabled(false)
    } else {
      setIsDisabled(true)
    }
  }, [newPassword, confirmPassword])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setShowPastPasswordError(false)
    const { name, value } = e.target
    if (name === InputsNames.NEW) {
      setNewPassword(value)
    } else if (name === InputsNames.CONFIRM) {
      setConfirmPassword(value)
    }
  }

  const handleResetPassword = async (event: React.FormEvent) => {
    event.preventDefault()
    setLoading(true)

    try {
      const response = await updatePasswordRequest({
        ds_cod_senh: emailFromStorage.code,
        ds_senh_nova: newPassword,
        ds_confirm_senha: confirmPassword,
        id_usua: emailFromStorage.id_usua
      })

      const { return_code } = response

      // NOTE: When the new password is the same as the old one
      if (return_code === 512) {
        setShowPastPasswordError(true)
      } else {
        dispatch(clearState())
        navigate(forgetPassword.getLink('changedPassword'))
      }
    } catch (error) {
      toast.error('Erro na alteração de senha')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <form onSubmit={handleResetPassword} autoComplete="true">
        <S.FieldsWrapper>
          <Typography className="forgetTitle" design="hav-h5-text">
            Criar nova senha
          </Typography>

          <Typography className="forgetDescription">
            Muito bem, <strong>{emailFromStorage.nm_usua}</strong>!
            <br /> Agora você pode criar uma nova senha de acesso.
          </Typography>
          <Password
            name={InputsNames.NEW}
            value={newPassword}
            label="Nova senha"
            onChange={handleInputChange}
            placeholder=""
            InputProps={{
              placeholder: 'Insira seu e-mail',
              autoFocus: true,
              inputProps: { maxLength: 100 }
            }}
            errorOutline={showPastPasswordError}
          />
          <S.ValidateWrapper>
            <Typography className="validateTitle" design="hav-h4-text">
              Sua senha deve conter:
            </Typography>
            <div>
              <span
                className={hasNumber(newPassword, securityParameters) ? 'rulePassed' : ''}
              >
                {IconCheck[hasNumber(newPassword, securityParameters) ? 'true' : 'false']}
                Números
              </span>
              <span
                className={
                  hasSpecialCharacter(newPassword, securityParameters) ? 'rulePassed' : ''
                }
              >
                {
                  IconCheck[
                    hasSpecialCharacter(newPassword, securityParameters)
                      ? 'true'
                      : 'false'
                  ]
                }
                Caracteres especiais
              </span>
              <span
                className={
                  hasUpperCase(newPassword, securityParameters) ? 'rulePassed' : ''
                }
              >
                {
                  IconCheck[
                    hasUpperCase(newPassword, securityParameters) ? 'true' : 'false'
                  ]
                }
                Letras maiúsculas
              </span>
              <span
                className={
                  hasMinLength(newPassword, Number(minLengthPassword)) ? 'rulePassed' : ''
                }
              >
                {
                  IconCheck[
                    hasMinLength(newPassword, Number(minLengthPassword))
                      ? 'true'
                      : 'false'
                  ]
                }
                Mínimo {minLengthPassword} caracteres
              </span>
              <span
                className={
                  hasLowerCase(newPassword, securityParameters) ? 'rulePassed' : ''
                }
              >
                {
                  IconCheck[
                    hasLowerCase(newPassword, securityParameters) ? 'true' : 'false'
                  ]
                }
                Letras minúsculas
              </span>
            </div>
          </S.ValidateWrapper>
          {showPastPasswordError && (
            <S.ErrorWrapper className="removeBigSpace">
              <Alert
                kind="error"
                fullWidth
                title="Senha já utilizada."
                message="Você já utilizou essa senha anteriormente. Favor cadastrar uma senha diferente."
              />
            </S.ErrorWrapper>
          )}
          <Password
            name={InputsNames.CONFIRM}
            value={confirmPassword}
            label="Confirmar senha"
            onChange={handleInputChange}
            placeholder=""
            InputProps={{
              placeholder: 'Insira seu e-mail',
              autoFocus: true,
              inputProps: { maxLength: 100 }
            }}
            errorOutline={showPastPasswordError}
          />
        </S.FieldsWrapper>
        <S.ButtonWrapper>
          <Button
            fullWidth
            content="Resetar senha"
            type="submit"
            loading={loading}
            disabled={isDisabled}
          />
        </S.ButtonWrapper>
      </form>
    </AuthLayout>
  )
}

export default ResetPassword
